import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const STORAGE_KEY = 'shipping-calculator:last-cep';

const formatCep = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

const formatMoney = (cents) => {
  const value = Number(cents) || 0;
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatPriceFromString = (priceString) => {
  // Shopify /cart/shipping_rates.json returns price as a string like "8.71"
  const value = Number(priceString);
  if (Number.isNaN(value)) return priceString;
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

// Fallback for when ViaCEP is unreachable. Based on Correios postal code allocation:
// https://www.correios.com.br/enviar-e-receber/precisa-de-ajuda/o-que-e-cep-e-por-que-usa-lo/estrutura-do-cep
const ufFromCepPrefix = (cep) => {
  const n = parseInt(String(cep).slice(0, 5), 10);
  if (!Number.isFinite(n)) return '';
  if (n <= 19999) return 'SP';
  if (n <= 28999) return 'RJ';
  if (n <= 29999) return 'ES';
  if (n <= 39999) return 'MG';
  if (n <= 48999) return 'BA';
  if (n <= 49999) return 'SE';
  if (n <= 56999) return 'PE';
  if (n <= 57999) return 'AL';
  if (n <= 58999) return 'PB';
  if (n <= 59999) return 'RN';
  if (n <= 63999) return 'CE';
  if (n <= 64999) return 'PI';
  if (n <= 65999) return 'MA';
  if (n <= 68899) return 'PA';
  if (n <= 68999) return 'AP';
  if (n <= 69299) return 'AM';
  if (n <= 69399) return 'RR';
  if (n <= 69899) return 'AM';
  if (n <= 69999) return 'AC';
  if (n <= 72799) return 'DF';
  if (n <= 72999) return 'GO';
  if (n <= 73699) return 'DF';
  if (n <= 76799) return 'GO';
  if (n <= 76999) return 'RO';
  if (n <= 77999) return 'TO';
  if (n <= 78899) return 'MT';
  if (n <= 78999) return 'RO';
  if (n <= 79999) return 'MS';
  if (n <= 87999) return 'PR';
  if (n <= 89999) return 'SC';
  if (n <= 99999) return 'RS';
  return '';
};

const formatEta = (deliveryRange) => {
  if (!Array.isArray(deliveryRange) || deliveryRange.length === 0) return '';
  const [min, max] = deliveryRange;
  const days = max ?? min;
  if (!days) return '';
  if (min && max && min !== max) {
    return `${min} a ${max} dias úteis`;
  }
  return `${days} ${days === 1 ? 'dia útil' : 'dias úteis'}`;
};

class ShippingCalculator extends Component {
  /** @type {AbortController | undefined} */
  #activeRequest;

  /** @type {boolean} */
  #inFlight = false;

  connectedCallback() {
    super.connectedCallback();

    const input = this.refs.input;
    if (input instanceof HTMLInputElement) {
      input.addEventListener('input', () => {
        input.value = formatCep(input.value);
        this.#hideError();
      });

      const stored = this.#readStoredCep();
      if (stored && !input.value) input.value = formatCep(stored);
    }

    const form = this.refs.form;
    if (form instanceof HTMLFormElement) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.#calculate();
      });
    }

    const section = this.closest('.shopify-section');
    /** @type {(event: CustomEvent) => void} */
    const onVariantUpdate = (event) => {
      const resource = event.detail?.resource;
      if (resource?.id) {
        this.dataset.variantId = String(resource.id);
        this.#clearResults();
      }
    };
    section?.addEventListener(ThemeEvents.variantUpdate, onVariantUpdate);

    this.disconnectedCallback = () => {
      section?.removeEventListener(ThemeEvents.variantUpdate, onVariantUpdate);
      this.#activeRequest?.abort();
    };
  }

  #readStoredCep() {
    try {
      return localStorage.getItem(STORAGE_KEY) || '';
    } catch {
      return '';
    }
  }

  #storeCep(cep) {
    try {
      localStorage.setItem(STORAGE_KEY, cep);
    } catch {
      // ignore
    }
  }

  #setLoading(loading) {
    const button = this.refs.submit;
    const buttonLabel = this.refs.buttonLabel;
    const spinner = this.refs.spinner;

    if (button instanceof HTMLButtonElement) button.disabled = loading;
    if (buttonLabel instanceof HTMLElement) buttonLabel.hidden = loading;
    if (spinner instanceof HTMLElement) spinner.hidden = !loading;
  }

  #showError(message) {
    const errorEl = this.refs.error;
    if (errorEl instanceof HTMLElement) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
    this.#clearResults();
  }

  #hideError() {
    const errorEl = this.refs.error;
    if (errorEl instanceof HTMLElement) {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
  }

  #clearResults() {
    const results = this.refs.results;
    if (results instanceof HTMLElement) {
      results.innerHTML = '';
      results.hidden = true;
    }
  }

  #renderResults(rates, { qualifiesForFreeShipping = false } = {}) {
    const results = this.refs.results;
    const rowTemplate = this.refs.rowTemplate;
    const emptyTemplate = this.refs.emptyTemplate;
    if (!(results instanceof HTMLElement)) return;

    results.innerHTML = '';

    if (!rates || rates.length === 0) {
      if (emptyTemplate instanceof HTMLTemplateElement) {
        results.appendChild(emptyTemplate.content.cloneNode(true));
      }
      results.hidden = false;
      return;
    }

    if (!(rowTemplate instanceof HTMLTemplateElement)) return;

    const sorted = [...rates].sort((a, b) => Number(a.price) - Number(b.price));

    sorted.forEach((rate, index) => {
      const fragment = rowTemplate.content.cloneNode(true);
      const li = fragment.querySelector('li');
      const nameEl = fragment.querySelector('[data-name]');
      const priceEl = fragment.querySelector('[data-price]');
      const sepEl = fragment.querySelector('.shipping-calculator__result-separator');
      const etaEl = fragment.querySelector('[data-eta]');

      if (nameEl) nameEl.textContent = rate.presentment_name || rate.name;

      const priceValue = Number(rate.price);
      const isShopifyFree = Number.isFinite(priceValue) && priceValue === 0;
      const isFrontFree = qualifiesForFreeShipping && index === 0;
      const isFree = isShopifyFree || isFrontFree;

      if (isFree) {
        if (priceEl) priceEl.textContent = 'GRÁTIS';
        if (li) li.classList.add('shipping-calculator__result--free');
        if (sepEl) sepEl.textContent = ' ';
      } else if (priceEl) {
        priceEl.textContent = formatPriceFromString(rate.price);
      }

      const eta = formatEta(rate.delivery_range);
      if (etaEl) {
        if (eta) {
          etaEl.textContent = eta;
        } else {
          etaEl.remove();
        }
      }
      results.appendChild(fragment);
    });

    results.hidden = false;
  }

  async #calculate() {
    if (this.#inFlight) return;

    const input = this.refs.input;
    if (!(input instanceof HTMLInputElement)) return;

    const rawCep = input.value.replace(/\D/g, '');
    if (rawCep.length !== 8) {
      this.#showError('Digite um CEP válido com 8 dígitos.');
      return;
    }

    const variantId = this.dataset.variantId;
    if (!variantId) {
      this.#showError('Selecione uma variante para calcular o frete.');
      return;
    }

    this.#hideError();
    this.#clearResults();
    this.#setLoading(true);
    this.#inFlight = true;

    this.#activeRequest?.abort();
    this.#activeRequest = new AbortController();
    const signal = this.#activeRequest.signal;

    let originalQuantity = 0;
    let didAdd = false;

    try {
      const cartRes = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        signal,
        headers: { 'Accept': 'application/json' },
      });
      if (!cartRes.ok) throw new Error('Não foi possível ler o carrinho.');
      const cart = await cartRes.json();
      const existing = cart.items?.find((item) => String(item.variant_id) === String(variantId));
      originalQuantity = existing?.quantity ?? 0;

      if (!existing) {
        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          signal,
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
        });
        const addData = await addRes.json().catch(() => ({}));
        if (!addRes.ok) {
          throw new Error(addData.description || 'Não foi possível calcular o frete agora.');
        }
        didAdd = true;
      }

      this.#storeCep(rawCep);

      let cartTotalCents = cart.total_price ?? 0;
      if (didAdd) {
        try {
          const cartAfter = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
            signal,
            headers: { 'Accept': 'application/json' },
          });
          if (cartAfter.ok) {
            const cartAfterData = await cartAfter.json();
            cartTotalCents = cartAfterData.total_price ?? cartTotalCents;
          }
        } catch {
          // best-effort; fall back to pre-add total
        }
      }

      const threshold = parseInt(this.dataset.freeShippingThreshold || '0', 10);
      const enabled = this.dataset.freeShippingEnabled === 'true';
      const qualifiesForFreeShipping = enabled && threshold > 0 && cartTotalCents >= threshold;

      const rates = await this.#fetchShippingRates(rawCep, signal);
      this.#renderResults(rates, { qualifiesForFreeShipping });
    } catch (err) {
      if (err?.name === 'AbortError') return;
      this.#showError(err?.message || 'Não foi possível calcular o frete agora.');
    } finally {
      if (didAdd) {
        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: Number(variantId), quantity: originalQuantity }),
          });
        } catch {
          // best-effort revert; ignore.
        }
      }
      this.#setLoading(false);
      this.#inFlight = false;
    }
  }

  async #resolveUf(zip, signal) {
    try {
      const res = await fetch(`https://viacep.com.br/ws/${zip}/json/`, { signal });
      if (res.ok) {
        const data = await res.json();
        if (data && !data.erro && data.uf) return data.uf;
      }
    } catch (e) {
      if (e?.name === 'AbortError') throw e;
      // fall through to local prefix mapping
    }
    return ufFromCepPrefix(zip);
  }

  async #fetchShippingRates(zip, signal) {
    // Async endpoints (recommended over legacy /cart/shipping_rates.json):
    // 1. POST /cart/prepare_shipping_rates.json — params go in the body (form-encoded).
    // 2. GET /cart/async_shipping_rates.json — params in the query string.
    //    Returns 200 with shipping_rates=null while computing, then with the array.
    // Shopify requires province (UF) for BR addresses; resolve it from the CEP.
    const uf = await this.#resolveUf(zip, signal);
    if (!uf) throw new Error('CEP não encontrado.');

    const addressBody = new URLSearchParams({
      'shipping_address[zip]': zip,
      'shipping_address[country]': 'Brazil',
      'shipping_address[province]': uf,
    });

    const prepareRes = await fetch('/cart/prepare_shipping_rates.json', {
      method: 'POST',
      signal,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: addressBody.toString(),
    });
    if (!prepareRes.ok) {
      const data = await prepareRes.json().catch(() => ({}));
      const zipError = data?.shipping_address?.zip || data?.zip;
      if (zipError) throw new Error('CEP inválido para entrega.');
      const countryError = data?.shipping_address?.country;
      if (countryError) throw new Error('Não há entrega configurada para este país.');
      throw new Error('Não foi possível calcular o frete agora.');
    }

    const maxAttempts = 20;
    const delayMs = 600;
    const pollUrl = `/cart/async_shipping_rates.json?${addressBody.toString()}`;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const res = await fetch(pollUrl, {
        signal,
        headers: { 'Accept': 'application/json' },
      });

      if (!res.ok) {
        throw new Error('Não foi possível calcular o frete agora.');
      }

      // While the rates are still being computed, Shopify can return a literal `null`
      // body (not always `{ shipping_rates: null }`), so guard before reading the field.
      const data = await res.json().catch(() => null);
      const rates = data?.shipping_rates;
      if (rates != null) {
        return rates;
      }

      await new Promise((r) => setTimeout(r, delayMs));
    }

    throw new Error('Tempo esgotado ao calcular o frete. Tente novamente.');
  }
}

if (!customElements.get('shipping-calculator')) {
  customElements.define('shipping-calculator', ShippingCalculator);
}
